import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { supabase, isConfigured, getAdminRole, type AdminRole } from './services/supabase';
import { UserProfile, AuthState } from './types';

// Components
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import ListingsPage from './components/ListingsPage';
import StudioPage from './components/StudioPage';
import UploadPage from './components/UploadPage';
import LoginPage from './components/LoginPage';
import LandingPage from './components/LandingPage';
import CreditsModal from './components/CreditsModal';
import ReferralsPage from './components/ReferralsPage';
import { ReferralPromoPopup } from '@rehub/announcements';
import Footer from './components/Footer';
import FluidBackground from './components/FluidBackground';
import AdminPanel from './components/AdminPanel';
import HdrPricingPage from './components/HdrPricingPage';
import { getMyHdrEntitlement, type HdrEntitlement } from './services/hdr';
import { trackHdrEvent } from './services/analytics';

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [adminRole, setAdminRole] = useState<AdminRole | null>(null);
  const [adminRoleLoaded, setAdminRoleLoaded] = useState(false);
  const [hdrEntitlement, setHdrEntitlement] = useState<HdrEntitlement | null>(null);

  // Initialize auth
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const initAuth = async () => {
      if (!isConfigured) {
        console.warn('Supabase not configured');
        setAuthState({ user: null, isLoading: false, isAuthenticated: false });
        return;
      }

      // Set a timeout to prevent infinite loading
      timeoutId = setTimeout(() => {
        console.warn('Auth timeout - resetting to login');
        setAuthState({ user: null, isLoading: false, isAuthenticated: false });
        window.history.replaceState({}, '', '/login');
      }, 10000); // 10 second timeout

      try {
        // Check for auth callback in URL (magic link with PKCE code)
        const queryParams = new URLSearchParams(window.location.search);
        const code = queryParams.get('code');
        
        if (code) {
          console.log('Auth code detected, exchanging for session...');
          // Exchange the code for a session
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          
          if (error) {
            console.error('Code exchange error:', error);
            clearTimeout(timeoutId);
            setAuthState({ user: null, isLoading: false, isAuthenticated: false });
            window.history.replaceState({}, '', '/login');
            return;
          }
          
          if (data.session?.user) {
            console.log('Session created for:', data.session.user.email);
            const profile = await fetchUserProfile(data.session.user.id);

            // Apply referral code if stored in localStorage (from ?ref= on login page)
            const storedRefCode = localStorage.getItem('referral_code');
            if (storedRefCode) {
              try {
                const { data: refResult } = await supabase.rpc('apply_referral_code', {
                  p_code: storedRefCode,
                  p_auth_method:
                    data.session.user?.app_metadata?.provider === 'google'
                      ? 'google'
                      : data.session.user?.app_metadata?.provider === 'email'
                        ? 'magic_link'
                        : null,
                  p_app_source: 'hdrphoto',
                });
                console.log('Referral code applied:', refResult);
              } catch (refErr) {
                console.warn('Referral code application failed:', refErr);
              } finally {
                localStorage.removeItem('referral_code');
              }
            }

            clearTimeout(timeoutId);
            setAuthState({
              user: profile || { id: data.session.user.id, email: data.session.user.email || '', credits: 0 },
              isLoading: false,
              isAuthenticated: true,
            });
            window.history.replaceState({}, '', '/listings');
            navigate('/listings');
            return;
          }
        }

        // No code - check for existing session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        clearTimeout(timeoutId);
        
        if (error) {
          console.error('Session error:', error);
          setAuthState({ user: null, isLoading: false, isAuthenticated: false });
          return;
        }
        
        if (session?.user) {
          console.log('Existing session found for:', session.user.email);
          const profile = await fetchUserProfile(session.user.id);
          setAuthState({
            user: profile || { id: session.user.id, email: session.user.email || '', credits: 0 },
            isLoading: false,
            isAuthenticated: true,
          });
        } else {
          console.log('No session found');
          setAuthState({ user: null, isLoading: false, isAuthenticated: false });
        }
      } catch (error) {
        console.error('Auth init error:', error);
        clearTimeout(timeoutId);
        setAuthState({ user: null, isLoading: false, isAuthenticated: false });
      }
    };

    initAuth();

    // Listen for auth changes (sign out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event);
        
        if (event === 'SIGNED_OUT') {
          setAuthState({ user: null, isLoading: false, isAuthenticated: false });
          navigate('/login');
        }
      }
    );
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [navigate]);

  const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.warn('Profile not found, using auth user data');
        // Profile doesn't exist yet - get email from auth user
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          return {
            id: user.id,
            email: user.email || '',
            credits: 0,
          };
        }
        return null;
      }
      return data as UserProfile;
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Fallback to auth user
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          return {
            id: user.id,
            email: user.email || '',
            credits: 0,
          };
        }
      } catch (e) {
        console.error('Fallback auth failed:', e);
      }
      return null;
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshCredits = async () => {
    if (authState.user) {
      const profile = await fetchUserProfile(authState.user.id);
      if (profile) {
        setAuthState(prev => ({ ...prev, user: profile }));
      }
      const entitlement = await getMyHdrEntitlement();
      setHdrEntitlement(entitlement);
    }
  };

  // Resolve admin role once signed in (shared admin_users table).
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.user?.id) {
      setAdminRole(null);
      setAdminRoleLoaded(false);
      return;
    }
    let cancelled = false;
    setAdminRoleLoaded(false);
    getAdminRole(authState.user.id).then((role) => {
      if (!cancelled) {
        setAdminRole(role);
        setAdminRoleLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [authState.isAuthenticated, authState.user?.id]);

  // The HDR allowance is applied server-side when a merge finishes, so poll
  // both the ReHub Credits wallet and the HDR image entitlement.
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.user?.id) return;
    const userId = authState.user.id;
    const refresh = async () => {
      const profile = await fetchUserProfile(userId);
      if (profile) {
        setAuthState(prev => (prev.user ? { ...prev, user: profile } : prev));
      }
      const entitlement = await getMyHdrEntitlement();
      setHdrEntitlement(entitlement);
    };
    refresh();
    const interval = setInterval(refresh, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated, authState.user?.id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('hdr_subscribed') === '1') {
      trackHdrEvent('hdr_subscription_started', { plan: params.get('plan') });
      refreshCredits();
      window.history.replaceState({}, '', location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Loading state
  if (authState.isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Login page
  if (location.pathname === '/login') {
    if (authState.isAuthenticated) {
      return <Navigate to="/listings" replace />;
    }
    return <LoginPage />;
  }

  // Public pricing page — the in-app /pricing route used to be gated behind
  // auth, so a signed-out visit never mounted HdrPricingPage.
  if (location.pathname === '/pricing' && !authState.isAuthenticated) {
    return (
      <div className="relative min-h-screen bg-slate-900">
        <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-slate-950/70 backdrop-blur-md">
          <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
            <a href="/" className="flex items-center">
              <img
                src="/hdr-photo-logo.png"
                alt="HDR Photo Studio"
                className="h-11 w-auto object-contain sm:h-12"
              />
            </a>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white sm:px-4"
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => navigate('/login?mode=signup')}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500"
              >
                Try HDR Photo
              </button>
            </div>
          </div>
        </header>
        <HdrPricingPage signedIn={false} />
      </div>
    );
  }

  // Public marketing landing page for unauthenticated visitors
  if (!authState.isAuthenticated) {
    return (
      <LandingPage
        onSignUp={() => navigate('/login?mode=signup')}
        onLogin={() => navigate('/login')}
      />
    );
  }

  return (
    <div className="relative min-h-screen flex">
      {/* Interactive fluid background that follows the cursor */}
      <FluidBackground />

      {/* Sidebar Navigation */}
      <Sidebar currentPath={location.pathname} isAdmin={!!adminRole} />
      
      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header 
          user={authState.user} 
          onSignOut={handleSignOut}
          onRefreshCredits={refreshCredits}
          onOpenBuyCredits={() => { setShowCreditsModal(true); refreshCredits(); }}
          isAdmin={!!adminRole}
          hdrEntitlement={hdrEntitlement}
        />
        
        {/* Page Content */}
        <main className="flex-1 overflow-auto flex flex-col">
          <div className="flex-1">
          <Routes>
            <Route path="/" element={<Navigate to="/listings" replace />} />
            <Route 
              path="/listings" 
              element={<ListingsPage userId={authState.user?.id || ''} entitlement={hdrEntitlement} />} 
            />
            <Route 
              path="/studio/:mediaId" 
              element={
                <StudioPage 
                  userId={authState.user?.id || ''} 
                  credits={authState.user?.credits || 0}
                  onCreditUsed={refreshCredits}
                />
              } 
            />
            <Route 
              path="/upload" 
              element={
                <UploadPage 
                  userId={authState.user?.id || ''} 
                  credits={authState.user?.credits || 0}
                  entitlement={hdrEntitlement}
                  onBuyCredits={() => setShowCreditsModal(true)}
                />
              } 
            />
            <Route
              path="/pricing"
              element={<HdrPricingPage entitlement={hdrEntitlement} />}
            />
            <Route
              path="/referrals"
              element={<ReferralsPage userId={authState.user?.id || ''} />}
            />
            <Route
              path="/admin"
              element={<Navigate to="/admin/processing" replace />}
            />
            {['/admin/:section', '/admin/:section/:itemId'].map((adminPath) => (
              <Route
                key={adminPath}
                path={adminPath}
                element={
                  !adminRoleLoaded ? (
                    <div className="flex justify-center py-24">
                      <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : adminRole && authState.user ? (
                    <AdminPanel user={authState.user} adminRole={adminRole} />
                  ) : (
                    <Navigate to="/listings" replace />
                  )
                }
              />
            ))}
            <Route path="*" element={<Navigate to="/listings" replace />} />
          </Routes>
          </div>
          <Footer />
        </main>
      </div>

      {showCreditsModal && authState.user && (
        <CreditsModal user={authState.user} onClose={() => setShowCreditsModal(false)} onSuccess={refreshCredits} />
      )}

      <ReferralPromoPopup
        client={supabase}
        userId={authState.user?.id}
        appSource="hdrphoto"
        onInvite={() => navigate('/referrals')}
        suppressed={showCreditsModal || /^\/(referrals|admin|upload|studio)\b/.test(location.pathname)}
      />
    </div>
  );
};

export default App;
